import React, { useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import {
  Dropdown,
  DropdownItem,
  DropdownToggle,
  DropdownMenu,
  UncontrolledTooltip,
} from "reactstrap";
import { WALLET_SCOPING_FEATURE } from "../../constants";
import { useSelector } from "react-redux";
import { isFeatureEnabled } from "../../redux/slice/dashboardSlice";
import { withFeature } from "../../hoc/withFeature";
import { WalletScope, WALLET_SCOPE_TYPE } from "../../utils";
import { PlanTag } from "../index";
import clx from "classnames";

import styles from "./index.module.scss";

interface WalletScopingSettingsProps {
  walletScope: WalletScope | null | undefined;
  onChange: (walletScope: WalletScope | null) => void;
  allowedFeatures: string[];
  originalWalletScope?: WalletScope | null | undefined; // Original scope from server to detect immutable state
}

const WALLET_SCOPE_TYPE_MAP = {
  GLOBAL: {
    value: "GLOBAL",
    displayName: "Global",
    description:
      "Users keep the same wallet when switching between your applications",
    icon: "🌐",
    recommended: true,
    riskLevel: "none",
  },
  THIS_APPLICATION: {
    value: "THIS_APPLICATION",
    displayName: "This Application",
    description: "Users get a dedicated wallet just for this application",
    icon: "🔒",
    recommended: false,
    riskLevel: "medium",
  },
  OTHER_APPLICATION: {
    value: "OTHER_APPLICATION",
    displayName: "Other Application",
    description: "Users share wallets with a specific app you choose",
    icon: "🔗",
    recommended: false,
    riskLevel: "high",
  },
};

const WalletScopingSettings: React.FC<WalletScopingSettingsProps> = ({
  walletScope,
  onChange,
  allowedFeatures,
  originalWalletScope,
}) => {
  const isEnabled = useSelector(isFeatureEnabled(WALLET_SCOPING_FEATURE));
  const [isActive, setActive] = useState(false);

  // Check if scope is immutable (already set to a scoped type)
  const isImmutable = !!(
    originalWalletScope &&
    originalWalletScope.type &&
    (originalWalletScope.type === WALLET_SCOPE_TYPE.THIS_APPLICATION ||
      originalWalletScope.type === WALLET_SCOPE_TYPE.OTHER_APPLICATION)
  );

  const onToggle = () => {
    if (!isImmutable) {
      setActive(!isActive);
    }
  };

  const handleTypeChange = (type: string) => {
    if (type === "GLOBAL") {
      onChange(null);
    } else if (type === "THIS_APPLICATION") {
      onChange({
        type: WALLET_SCOPE_TYPE.THIS_APPLICATION,
      });
    } else if (type === "OTHER_APPLICATION") {
      onChange({
        type: WALLET_SCOPE_TYPE.OTHER_APPLICATION,
        targetAppId: "",
      });
    }
  };

  const handleTargetAppIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (walletScope?.type === WALLET_SCOPE_TYPE.OTHER_APPLICATION) {
      const value = event.target.value;
      onChange({
        ...walletScope,
        targetAppId: value, // Backend computes application.appId from database
      });
    }
  };

  const getCurrentType = (): keyof typeof WALLET_SCOPE_TYPE_MAP => {
    if (!walletScope) return "GLOBAL";
    // Map enum values to string keys for WALLET_SCOPE_TYPE_MAP
    switch (walletScope.type) {
      case WALLET_SCOPE_TYPE.THIS_APPLICATION:
        return "THIS_APPLICATION";
      case WALLET_SCOPE_TYPE.OTHER_APPLICATION:
        return "OTHER_APPLICATION";
      case WALLET_SCOPE_TYPE.GLOBAL:
      default:
        return "GLOBAL";
    }
  };

  const options = Object.values(WALLET_SCOPE_TYPE_MAP).map((option) => (
    <DropdownItem
      key={option.value}
      onClick={() => handleTypeChange(option.value)}
      value={option.value}
    >
      {option.displayName}
    </DropdownItem>
  ));

  const currentOption = WALLET_SCOPE_TYPE_MAP[getCurrentType()];
  const displayVal = currentOption?.displayName || "Select wallet isolation";

  return (
    <div className={styles.walletScopingSettings}>
      <div className={clx("label-wrapper")}>
        <label
          style={{
            fontSize: "14px",
            marginBottom: "0px",
          }}
        >
          User Wallet Isolation
        </label>

        <AiOutlineQuestionCircle
          style={{ marginBottom: "0px" }}
          id="wallet-scoping-tooltip"
        />
        <UncontrolledTooltip
          popperClassName="tooltip-popper"
          target="wallet-scoping-tooltip"
          placement="top"
          autohide={false}
        >
          <div style={{ maxWidth: "300px", textAlign: "left" }}>
            <div style={{ fontWeight: "bold", marginBottom: "8px" }}>
              Control wallet isolation between your applications:
            </div>
            <div style={{ marginBottom: "6px" }}>
              🌐 <strong>Global:</strong> Same wallet across all your apps
              (recommended)
            </div>
            <div style={{ marginBottom: "6px" }}>
              🔒 <strong>This Application:</strong> Separate wallet just for
              this app
            </div>
            <div style={{ marginBottom: "8px" }}>
              🔗 <strong>Other Application:</strong> Share wallet with another
              specific app
            </div>
            <div
              style={{ fontSize: "12px", color: "#666", fontStyle: "italic" }}
            >
              ⚠️ Scope changes are permanent and affect existing users' wallet
              access
            </div>
          </div>
        </UncontrolledTooltip>

        <PlanTag locked={!isEnabled} id="wallet-scoping" />
      </div>

      <div style={{ marginTop: "8px" }}>
        <Dropdown id="walletScopingSelect" isOpen={isActive} toggle={onToggle}>
          <DropdownToggle
            caret
            className="w-100 d-flex justify-content-between align-items-center"
            color=""
            disabled={!isEnabled || isImmutable}
          >
            {displayVal}
          </DropdownToggle>
          <DropdownMenu>{options}</DropdownMenu>
        </Dropdown>

        {/* Immutability warning for apps with existing scoped wallets */}
        {isImmutable && (
          <div
            style={{
              marginTop: "8px",
              padding: "8px 12px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                color: "#495057",
                marginBottom: "4px",
              }}
            >
              🔒 Wallet scope is permanent
            </div>
            <div style={{ color: "#6c757d" }}>
              This setting cannot be changed to maintain wallet consistency for
              existing users.
            </div>
          </div>
        )}

        {getCurrentType() === "OTHER_APPLICATION" && (
          <div style={{ marginTop: "8px" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: "500",
                marginBottom: "4px",
                display: "block",
              }}
            >
              Target App ID <span style={{ color: "#dc3545" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Enter the App ID to share wallets with (e.g., abc123-def456-ghi789)"
              value={walletScope?.targetAppId || ""}
              onChange={handleTargetAppIdChange}
              disabled={!isEnabled}
              className={`form-control ${styles.targetAppInput}`}
              style={{ fontSize: "14px" }}
            />
            <div
              style={{
                marginTop: "4px",
                fontSize: "11px",
                color: "#6c757d",
                fontStyle: "italic",
              }}
            >
              Find the App ID in the other app's settings under "App Info"
            </div>
          </div>
        )}

        {/* Impact warning for non-global settings */}
        {getCurrentType() !== "GLOBAL" && !isImmutable && (
          <div
            style={{
              marginTop: "12px",
              padding: "8px 12px",
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeaa7",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <div
              style={{
                fontWeight: "500",
                color: "#856404",
                marginBottom: "4px",
              }}
            >
              ⚠️ Important: Permanent change with impact on existing users
            </div>
            <div style={{ color: "#856404" }}>
              This change is permanent and cannot be undone. Existing users will
              lose access to their current wallet funds in this app. They'll
              need to transfer assets manually if needed.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withFeature(WalletScopingSettings, WALLET_SCOPING_FEATURE);
